import React, { useState, useEffect } from 'react';
import { Input } from '../ui/Input';
import { PhoneInput } from '../ui/PhoneInput';
import { AddressAutocomplete } from '../ui/AddressAutocomplete';
import { Button } from '../ui/Button';
import { apiCreate } from '../../api/apiClient';

interface IntegratorFormProps {
  initialData?: any;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}

const StepIndicator: React.FC<{ step: number; total: number }> = ({ step, total }) => (
  <div className="flex items-center justify-center mb-8" aria-label={`Step ${step} of ${total}`}>
    {Array.from({ length: total }, (_, i) => i + 1).map(s => (
      <React.Fragment key={s}>
        <div aria-current={s === step ? 'step' : undefined} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${s === step ? 'bg-brand-600 text-white' : s < step ? 'bg-brand-100 text-brand-700' : 'bg-gray-200 text-gray-500'}`}>{s}</div>
        {s < total && <div className={`w-12 h-0.5 ${s < step ? 'bg-brand-400' : 'bg-gray-200'}`} />}
      </React.Fragment>
    ))}
  </div>
);

function generatePassword() {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export const IntegratorForm: React.FC<IntegratorFormProps> = ({ initialData, onSubmit, onCancel }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [createdData, setCreatedData] = useState<any>(null);
  const [onboardUrl, setOnboardUrl] = useState('');

  const [companyName, setCompanyName] = useState(initialData?.company_name || '');
  const [contactEmail, setContactEmail] = useState(initialData?.contact_email || '');
  const [phone, setPhone] = useState(initialData?.phone || '');
  const [address, setAddress] = useState(initialData?.address || '');
  const [adminFirst, setAdminFirst] = useState('');
  const [adminLast, setAdminLast] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState(generatePassword());
  const [autoGen, setAutoGen] = useState(true);

  const validate = (s: number) => {
    const e: Record<string, string> = {};
    if (s === 1 && !companyName.trim()) e.companyName = 'Required';
    if (s === 2 && !contactEmail.trim()) e.contactEmail = 'Required';
    if (s === 3) {
      if (!adminFirst.trim()) e.adminFirst = 'Required';
      if (!adminLast.trim()) e.adminLast = 'Required';
      if (!adminEmail.trim()) e.adminEmail = 'Required';
      if (!adminPassword.trim()) e.adminPassword = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate(step)) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  useEffect(() => { if (step === 3 && !adminEmail) setAdminEmail(contactEmail); }, [step]);

  const handleCreate = async () => {
    if (!validate(3)) return;
    setLoading(true);
    try {
      // 1. Create integrator through the AWS API
      const integrator = await apiCreate('integrators', {
        name: companyName,
        email: contactEmail,
        phone,
        address,
        status: 'active',
      });

      // 2. Create admin user
      const user = await apiCreate('users', {
        email: adminEmail,
        password: adminPassword,
        name: `${adminFirst} ${adminLast}`.trim(),
        first_name: adminFirst,
        last_name: adminLast,
        role: 'integrator',
        integrator_id: integrator.id,
        status: 'active',
      });

      setCreatedData({ integrator, user, stripeId: integrator.stripe_customer_id });
      setOnboardUrl(window.location.origin + '/login');
      setStep(4);
    } catch (err: any) {
      setErrors({ submit: err?.response?.data?.message || err.message || 'Failed to create dealer' });
    } finally {
      setLoading(false);
    }
  };

  const copyUrl = () => { navigator.clipboard.writeText(onboardUrl); };

  const stepTitles = ['Company Info', 'Contact Info', 'Admin User Setup'];

  if (initialData) {
    const handleEditSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!companyName.trim()) { setErrors({ companyName: 'Required' }); return; }
      setLoading(true);
      try {
        await onSubmit({
          name: companyName,
          company_name: companyName,
          contact_email: contactEmail,
          email: contactEmail,
          phone,
          address,
        });
      } catch (err: any) {
        setErrors({ submit: err?.response?.data?.message || err.message || 'Failed to update dealer' });
      } finally {
        setLoading(false);
      }
    };
    return (
      <form onSubmit={handleEditSubmit}>
        {errors.submit && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{errors.submit}</div>}
        <Input label="Company Name *" value={companyName} onChange={e => setCompanyName(e.target.value)} error={errors.companyName} />
        <Input label="Contact Email" type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
        <PhoneInput label="Phone" value={phone} onChange={setPhone} />
        <AddressAutocomplete
          label="Address"
          value={address}
          onChange={setAddress}
          onSelect={(p) => {
            const full = [p.street, p.city, p.state, p.zip].filter(Boolean).join(', ');
            setAddress(full || p.street);
          }}
        />
        <div className="flex gap-3 justify-end mt-6">
          <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
          <Button type="submit" loading={loading}>Update Dealer</Button>
        </div>
      </form>
    );
  }

  return (
    <div>
      {step <= 3 ? (
        <>
          <StepIndicator step={step} total={3} />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Step {step} of 3</h3>
          <p className="text-sm text-gray-500 mb-6">{stepTitles[step - 1]}</p>
        </>
      ) : (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Dealer created</h3>
          <p className="text-sm text-gray-500">Onboarding</p>
        </div>
      )}

      {errors.submit && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{errors.submit}</div>}

      {step === 1 && (
        <Input label="Company Name *" value={companyName} onChange={e => setCompanyName(e.target.value)} error={errors.companyName} />
      )}

      {step === 2 && (
        <div>
          <Input label="Contact Email *" type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} error={errors.contactEmail} />
          <PhoneInput label="Phone" value={phone} onChange={setPhone} />
          <AddressAutocomplete
          label="Address"
          value={address}
          onChange={setAddress}
          onSelect={(p) => {
            const full = [p.street, p.city, p.state, p.zip].filter(Boolean).join(', ');
            setAddress(full || p.street);
          }}
        />
        </div>
      )}

      {step === 3 && (
        <div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="First Name *" value={adminFirst} onChange={e => setAdminFirst(e.target.value)} error={errors.adminFirst} />
            <Input label="Last Name *" value={adminLast} onChange={e => setAdminLast(e.target.value)} error={errors.adminLast} />
          </div>
          <Input label="Admin Email *" type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} error={errors.adminEmail} />
          <div className="flex items-center gap-3 mb-2">
            <label className="text-sm text-gray-600"><input type="checkbox" checked={autoGen} onChange={e => { setAutoGen(e.target.checked); if (e.target.checked) setAdminPassword(generatePassword()); }} className="mr-2" />Auto-generate password</label>
          </div>
          <Input label="Password *" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} readOnly={autoGen} error={errors.adminPassword} />
        </div>
      )}

      {step === 4 && (
        <div>
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
            <p className="text-green-800 font-medium">✓ Dealer created successfully!</p>
            <ul className="mt-2 text-sm text-green-700 space-y-1">
              <li>• Company: {companyName}</li>
              <li>• Admin user: {adminEmail}</li>
              {createdData?.stripeId && <li>• Stripe customer: {createdData.stripeId}</li>}
            </ul>
          </div>
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm font-medium text-emerald-800 mb-2">Send this onboarding link to the dealer:</p>
            <div className="flex gap-2">
              <input readOnly value={onboardUrl} className="flex-1 px-3 py-2 bg-white border border-emerald-300 rounded text-sm text-emerald-900" />
              <Button size="sm" onClick={copyUrl}>Copy</Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3 justify-end mt-6">
        {step <= 3 && <Button variant="secondary" type="button" onClick={step === 1 ? onCancel : back}>{step === 1 ? 'Cancel' : 'Back'}</Button>}
        {step < 3 && <Button onClick={next}>Next</Button>}
        {step === 3 && <Button onClick={handleCreate} loading={loading}>Create Dealer</Button>}
        {/* Success is no longer presented as a fourth creation step. */}
        {step === 4 && <Button onClick={onCancel}>Done</Button>}
      </div>
    </div>
  );
};
